// src/App.js
import React, { useEffect, useMemo, useState } from "react";
import { ALBUMS_SEED } from "./data/albums.seed";
import { loadStore, saveStore } from "./utils/store";
import { fetchAlbums } from "./utils/fetchAlbums";

// Tabs & Components
import Button from "./components/Button";
import HomeTab from "./tabs/HomeTab";
// IMPORTANT: Using AlbumsTab (not Page) to keep player logic
import AlbumsTab from "./tabs/AlbumsTab";
import FavoritesTab from "./tabs/FavoritesTab";
import AboutTab from "./tabs/AboutTab";
import SettingsTab from "./tabs/SettingsTab";
// NEW: Import the ticker
import PhotoTicker from "./components/PhotoTicker";

const byYear = (a, b) => (a.year || 0) - (b.year || 0);

// Public CSV with Wikipedia “songs written by” list
const SONGS_CSV_URL =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/dylan/bobdylan_wikipedia_songs_written.csv";

/* ------------------------- tiny CSV parser ------------------------- */
function parseCsv(text) {
  const rows = [];
  let row = [];
  let cell = "";
  let i = 0;
  let inQuotes = false;

  const pushCell = () => {
    row.push(cell);
    cell = "";
  };
  const pushRow = () => {
    rows.push(row);
    row = [];
  };

  while (i < text.length) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      cell += ch;
      i++;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (ch === ",") {
      pushCell();
      i++;
      continue;
    }
    if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      pushCell();
      pushRow();
      i++;
      continue;
    }
    cell += ch;
    i++;
  }
  pushCell();
  if (row.length > 1 || (row.length === 1 && row[0] !== "")) pushRow();
  return rows;
}

/* -------------------- normalize + shape song rows ------------------- */
function normalizeSongsFromCsv(text) {
  const rows = parseCsv(text);
  if (!rows.length) return [];

  const headers = rows[0].map((h) => h.trim().toLowerCase());
  const idx = (name) => headers.indexOf(name.toLowerCase());

  const iTitle =
    idx("song_title") !== -1 ? idx("song_title") : idx("song title");
  const iAlbum =
    idx("album_release") !== -1 ? idx("album_release") : idx("album release");
  const iYear =
    idx("recorded_year") !== -1 ? idx("recorded_year") : idx("recorded");

  const items = [];
  for (let r = 1; r < rows.length; r++) {
    const line = rows[r];
    const title = (line[iTitle] || "").trim();
    const releaseTitle = (line[iAlbum] || "").trim();
    const recordedYearRaw = (line[iYear] || "").trim();
    const recordedYear = recordedYearRaw ? Number(recordedYearRaw) : null;

    if (!title) continue;

    items.push({
      id: `${title}|${releaseTitle}|${recordedYear ?? ""}`,
      title,
      releaseTitle,
      recordedYear,
      songHref: "",
      firstPlayed: null,
      lastPlayed: null,
      timesPlayed: null,
    });
  }

  const seen = new Set();
  const deduped = [];
  for (const s of items) {
    if (seen.has(s.id)) continue;
    seen.add(s.id);
    deduped.push(s);
  }
  return deduped;
}

/* ------------------------------ App -------------------------------- */
export default function App() {
  const [tab, setTab] = useState("albums");
  const [memoryMode, setMemoryMode] = useState(false);

  // App-level store
  const [store, setStore] = useState(() => ({
    albums: ALBUMS_SEED,
    ...(loadStore() || {}),
  }));

  // Global list of songs (from CSV)
  const [songs, setSongs] = useState([]);

  // Load larger list from /public/data/albums.json
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        if (!params.get("noAlbumsJson")) {
          const list = await fetchAlbums();
          if (alive && Array.isArray(list) && list.length) {
            setStore((s) => ({ ...s, albums: list }));
          }
        }
      } catch {}
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Load + cache CSV
  useEffect(() => {
    let alive = true;
    const LS_KEY = "dylanSongsCsvCache_v1";
    (async () => {
      try {
        const cached = localStorage.getItem(LS_KEY);
        if (cached) {
          const parsed = normalizeSongsFromCsv(cached);
          if (alive) setSongs(parsed);
        }
        const res = await fetch(SONGS_CSV_URL, {
          mode: "cors",
          cache: "no-store",
        });
        if (!res.ok) throw new Error("CSV fetch failed");
        const text = await res.text();
        localStorage.setItem(LS_KEY, text);
        const parsed = normalizeSongsFromCsv(text);
        if (alive) setSongs(parsed);
      } catch {}
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Body class for Memory Mode
  useEffect(() => {
    document.body.classList.toggle("memory-mode", memoryMode);
  }, [memoryMode]);

  // Ensure albums exist
  useEffect(() => {
    if (!store.albums) setStore((s) => ({ ...s, albums: ALBUMS_SEED }));
  }, []);

  const albums = useMemo(
    () => (store.albums || ALBUMS_SEED).slice().sort(byYear),
    [store.albums]
  );

  const jumpToSong = (_albumId, _songId) => setTab("albums");

  return (
    <>
      <a href="#main" className="skip">
        Skip to content
      </a>

      <header className="app-head" role="banner">
        <div className="wrap">
          <div className="brand" aria-label="Dylan Family Songbook">
            <div className="logo" aria-hidden />
            <div>
              <div
                style={{
                  fontSize: 14,
                  color: "var(--ink70)",
                  fontWeight: 800,
                  lineHeight: 1,
                }}
              >
                Dylan Family Songbook
              </div>
              <div className="xs muted" style={{ lineHeight: 1 }}>
                Albums · Years · Songs · Favorite Lines
              </div>
            </div>
          </div>

          <nav className="tabs" role="tablist">
            {[
              ["home", "Home"],
              ["albums", "Albums"],
              ["favorites", "Favorites"],
              ["about", "About"],
              ["settings", "Settings"],
            ].map(([id, label]) => (
              <button
                key={id}
                role="tab"
                aria-selected={tab === id}
                className={`tab ${tab === id ? "active" : ""}`}
                onClick={() => setTab(id)}
              >
                {label}
              </button>
            ))}
          </nav>

          <div style={{ display: "flex", gap: 8 }}>
            <Button
              variant="secondary"
              onClick={() => setMemoryMode((v) => !v)}
              aria-pressed={memoryMode}
            >
              {memoryMode ? "Memory Mode: On" : "Memory Mode"}
            </Button>
            <Button
              className="ghost"
              onClick={(e) => {
                e.preventDefault();
                window.print();
              }}
            >
              Print
            </Button>
          </div>
        </div>
      </header>

      {/* Added paddingBottom so the photo ticker doesn't cover content */}
      <main
        id="main"
        className="container"
        role="main"
        style={{ paddingBottom: "180px" }}
      >
        {tab === "home" && <HomeTab onOpenAlbums={() => setTab("albums")} />}

        {/* Using AlbumsTab to preserve Player logic */}
        {tab === "albums" && (
          <AlbumsTab albums={albums} songs={songs} onOpenAlbum={() => {}} />
        )}

        {tab === "favorites" && (
          <FavoritesTab store={store} onJumpTo={jumpToSong} />
        )}
        {tab === "about" && <AboutTab />}
        {tab === "settings" && (
          <SettingsTab
            store={store}
            setStore={(s) => {
              saveStore(s);
              setStore(s);
            }}
          />
        )}
      </main>

      {/* Floating Ticker at the bottom */}
      <PhotoTicker />
    </>
  );
}
